import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { AppHeader } from '@/components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

const smartPhraseSchema = z.object({
  code: z.string()
    .min(3, 'Code must be at least 3 characters')
    .max(20, 'Code must not exceed 20 characters')
    .regex(/^\.[A-Z0-9]+$/, 'Code must start with a dot and contain only uppercase letters and numbers'),
  name: z.string()
    .min(3, 'Name must be at least 3 characters')
    .max(100, 'Name must not exceed 100 characters'),
  content: z.string()
    .min(10, 'Content must be at least 10 characters')
    .max(10000, 'Content must not exceed 10,000 characters'),
})

type SmartPhraseFormData = z.infer<typeof smartPhraseSchema>

export default function New() {
  const [error, setError] = useState('')
  const { register, handleSubmit, formState: { errors: formErrors, isSubmitting } } = useForm<SmartPhraseFormData>({
    resolver: zodResolver(smartPhraseSchema),
    defaultValues: {
      code: '.',
      name: '',
      content: '',
    },
  })

  const onSubmit = async (data: SmartPhraseFormData) => {
    setError('')

    try {
      const response = await fetch('/admin/smart_phrases', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({ smart_phrase: data }),
      })

      if (response.ok) {
        router.visit('/admin/smart_phrases')
      } else {
        const errorData = await response.json()
        setError(errorData.errors?.join(', ') || 'Failed to create template')
      }
    } catch (err) {
      setError('An error occurred while creating the template')
    }
  }

  return (
    <>
      <AppHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin/console' },
          { label: 'SmartPhrases', href: '/admin/smart_phrases' },
          { label: 'New' },
        ]}
      />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              <Card className="bg-gradient-to-t from-primary/5 to-card shadow-xs">
                <CardHeader>
                  <CardTitle>Create SmartPhrase Template</CardTitle>
                  <CardDescription>
                    Create a reusable note template with a short code for quick insertion
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {error && (
                      <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                        {error}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="code">Template Code</Label>
                      <Input
                        id="code"
                        {...register('code')}
                        placeholder=".MYTEMPLATE"
                        className="font-mono"
                      />
                      <p className="text-xs text-muted-foreground">
                        Must start with a dot and use uppercase letters/numbers only (e.g., .PANOTE, .FOLLOWUP2WK)
                      </p>
                      {formErrors.code && (
                        <p className="text-sm text-destructive">{formErrors.code.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="name">Template Name</Label>
                      <Input
                        id="name"
                        {...register('name')}
                        placeholder="My Template Name"
                      />
                      <p className="text-xs text-muted-foreground">
                        A descriptive name for this template
                      </p>
                      {formErrors.name && (
                        <p className="text-sm text-destructive">{formErrors.name.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="content">Template Content</Label>
                      <Textarea
                        id="content"
                        {...register('content')}
                        placeholder="Enter your template content here. Use [PLACEHOLDERS] for variable information."
                        rows={15}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        Use square brackets for placeholders: [DATE], [MEDICATION], [ADHERENCE], etc.
                      </p>
                      {formErrors.content && (
                        <p className="text-sm text-destructive">{formErrors.content.message}</p>
                      )}
                    </div>

                    <div className="flex gap-4">
                      <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Creating...' : 'Create Template'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => router.visit('/admin/smart_phrases')}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
